import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Chip, Tooltip } from "@heroui/react";
import { GripVertical, Pencil, Ticket, Trash2 } from "lucide-react";

import {
  BannerDestination,
  getBannerPromoCode,
} from "@/components/banners/banner-destination";
import { Banner } from "@/types";

/** The column layout shared by the list header and every row. */
export const BANNER_ROW_GRID =
  "grid grid-cols-[28px_28px_152px_minmax(0,1fr)_150px_88px] items-center gap-4 px-2";

interface SortableBannerRowProps {
  banner: Banner;
  position: number;
  onEdit: (banner: Banner) => void;
  onDelete: (banner: Banner) => void;
}

export const SortableBannerRow = ({
  banner,
  position,
  onEdit,
  onDelete,
}: SortableBannerRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: banner.id });
  const promoCode = getBannerPromoCode(banner);

  return (
    <li
      ref={setNodeRef}
      className={`${BANNER_ROW_GRID} border-separator border-b py-3 ${
        isDragging
          ? "bg-overlay shadow-overlay relative z-10 rounded-2xl"
          : "bg-surface"
      }`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        ref={setActivatorNodeRef}
        aria-label="Drag to reorder"
        className={`text-muted hover:bg-surface-secondary flex touch-none items-center justify-center rounded-md p-1 outline-none focus-visible:ring-2 focus-visible:ring-(--focus) ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        type="button"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>
      <span className="text-muted text-sm tabular-nums">{position}</span>
      <a
        aria-label="Open banner image"
        className="rounded-xl"
        href={banner.imageUrl}
        rel="noreferrer"
        target="_blank"
      >
        <span className="bg-surface-secondary block aspect-[2/1] overflow-hidden rounded-xl">
          <img
            alt=""
            className="size-full object-cover"
            src={banner.imageUrl}
          />
        </span>
      </a>
      <BannerDestination banner={banner} />
      <div>
        {promoCode ? (
          <Chip color="success" variant="soft">
            <Ticket className="size-3.5" />
            {promoCode}
          </Chip>
        ) : (
          <span className="text-muted">—</span>
        )}
      </div>
      <div className="flex items-center justify-end gap-1">
        <Tooltip delay={300}>
          <Button
            isIconOnly
            aria-label="Edit banner"
            size="sm"
            variant="ghost"
            onPress={() => onEdit(banner)}
          >
            <Pencil className="size-4" />
          </Button>
          <Tooltip.Content>Edit banner</Tooltip.Content>
        </Tooltip>
        <Tooltip delay={300}>
          <Button
            isIconOnly
            aria-label="Delete banner"
            size="sm"
            variant="ghost"
            onPress={() => onDelete(banner)}
          >
            <Trash2 className="size-4" />
          </Button>
          <Tooltip.Content>Delete banner</Tooltip.Content>
        </Tooltip>
      </div>
    </li>
  );
};
